// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { FormFieldSizeVariant } from "@eisinc/MobiusOverrides/FormField";
import { sizeVariantValues } from "@eisinc/MobiusOverrides/FormField/formStyles";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { FormFieldClickable, FormFieldIcon } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { xBaseTheme } from "@midpoint/XDL/types";
import { color, getSystemColor } from "@midpoint/XDL/Utilities";
import { css } from "styled-components";

const PlaceholderSelector = `
        &::placeholder,
        &::-webkit-input-placeholder
`;

export const placeholderStyle = css`
    ${PlaceholderSelector} {
        font-size: ${fontConfig.singleLineSmall.size}px;
        font-style: italic;
        letter-spacing: 0;
        line-height: ${fontConfig.singleLineSmall.lineHeight} !important;
        max-width: 100%;
        color: ${getSystemColor("common.ui4")};
        font-weight: 400;
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        border: "rectangle",
        sizeVariant: "default",
        backgroundColor: "common.ui1",
        labelProps: {
            mergeCss: true,
            ...fontConfig.singleLinexSmall,
            transform: "capitalize",
            weight: fontConfig.weight.regular,
            color: "common.ui6",
            css: css`
                min-height: initial;
                letter-spacing: 0;
                display: flex;
                align-items: center;
                & .hint-wrapper {
                    margin-top: 1px;
                    display: flex;
                    flex-grow: 1;
                    justify-content: flex-end;
                }
            `,
        },
        errorProps: {
            mergeCss: true,
            ...fontConfig.singleLineSmall,
            weight: fontConfig.weight.regular,
            color: "danger.main",
            css: css`
                min-height: initial;
                letter-spacing: 0;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: ${({ labelPosition }: any) => (labelPosition === "left" ? "auto" : "100%")};
                input,
                textarea,
                select {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        ${placeholderStyle}
                        &:disabled {
                            ${PlaceholderSelector} {
                                color: ${color("common.ui6")};
                                font-style: normal;
                            }
                        }
                        &:not(:disabled):not(:focus):hover {
                            border: 1px solid ${color("common.ui5")};
                        }
                        &:hover:not([type="qty"]):not(:disabled) {
                            border: 1px solid ${color("common.ui8")} !important;
                        }
                    }
                }
                input[aria-autocomplete="list"] {
                    ${PlaceholderSelector} {
                        font-style: normal;
                    }
                }
                ${FormFieldClickable}[data-role="icon-clickable"] {
                    background: ${getSystemColor("primary.main")};
                    border: 1px solid ${getSystemColor("primary.main")};
                    border-radius: 0;
                    transition: all 0.3s ease;
                    ${FormFieldIcon} {
                        color: ${getSystemColor("common.white")};
                        background: transparent;
                    }
                    &:hover:not(:disabled) {
                        background: ${getSystemColor("common.ui8")};
                        border-color: ${getSystemColor("common.ui8")};
                        transition: all 0.3s ease;
                        ${FormFieldIcon} {
                            color: ${getSystemColor("common.white")};
                        }
                    }
                    &:focus:not(:disabled) {
                        background: ${getSystemColor("common.ui9")};
                        border-color: ${getSystemColor("common.ui9")};
                        transition: all 0.3s ease;
                        ${FormFieldIcon} {
                            color: ${getSystemColor("common.white")};
                        }
                    }
                    &[data-simulate-state="hover"]:not(:disabled) {
                        background: ${getSystemColor("common.ui8")};
                        border-color: ${getSystemColor("common.ui8")};
                        ${FormFieldIcon} {
                            color: ${getSystemColor("common.white")};
                        }
                    }
                }
                ${FormFieldClickable}[data-role="clear-clickable"] {
                    height: 32px;

                    &:hover svg {
                        color: ${getSystemColor("common.ui8")};
                    }
                    &[data-simulate-state="hover"] svg {
                        color: ${getSystemColor("common.ui8")} !important;
                    }
                }
                input,
                select {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        text-overflow: ellipsis;
                    }
                }
                input,
                textarea {
                    box-shadow: inset 0 1px 0 0 ${color("common.ui7", 0.1)};
                    resize: vertical;
                }

                &[data-size="large"] {
                    && input {
                        padding: 0 16px 0 16px;
                    }

                    && ${IconWrapper} {
                        padding: 10px;
                    }
                }

                .end-adornment {
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    display: flex;
                    align-items: center;
                }
                .clickable-wrapper {
                    position: absolute;
                    top: 0;
                    right: 0;
                    display: flex;
                    align-items: center;
                    gap: ${({ _sizeVariant }: any) =>
                        sizeVariantValues[_sizeVariant as FormFieldSizeVariant].iconPadding - 1}px;
                    padding: ${({ _sizeVariant }: any) =>
                        `0 0 0 ${sizeVariantValues[_sizeVariant as FormFieldSizeVariant].iconPadding - 2}px`};
                    & > ${FormFieldClickable} {
                        position: relative;
                        padding: 0;
                        width: 16px;
                        & ${FormFieldIcon} {
                            padding: 0;
                            width: auto;
                        }
                    }
                    & > ${FormFieldClickable}[data-role="icon-clickable"] {
                        width: 33px;
                        height: 34px;
                        border: 1px solid ${color("common.ui5")};
                        border-left: 0;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        ${FormFieldIcon} {
                            width: 18px;
                            height: 18px;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%);
                            svg {
                                width: 18px;
                                height: 18px;
                            }
                        }
                    }
                }
                input[data-test-selector="focus"],
                select[data-test-selector="focus"] {
                    border: 1px solid ${color("common.ui9")};
                }
                textarea[data-test-selector="focus"] {
                    border: 1px solid ${color("common.ui9")};
                }
                input[data-test-selector="hover"]:not(:focus):not([type="qty"]),
                select[data-test-selector="hover"]:not(:focus),
                textarea[data-test-selector="hover"]:not(:focus) {
                    border: 1px solid ${color("common.ui8")} !important;
                }
            `,
            formField: css`
                ${({ labelPosition }: any) => {
                    if (labelPosition === "left") {
                        return css`
                            align-items: baseline;
                        `;
                    }
                }}
                &&& select {
                    padding: 0 20px 0 4px;
                }
            `,
        },
    },
} as xBaseTheme["formField"];
